import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { imageUrl } from "../lib/photosApi";

const Lightbox = ({ photos, index, onClose, onNavigate }) => {
  const touchStartX = useRef(null);
  const count = photos.length;
  const photo = photos[index];

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") onNavigate((index + 1) % count);
      else if (e.key === "ArrowLeft") onNavigate((index - 1 + count) % count);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, count, onClose, onNavigate]);

  // Lock background scroll while the viewer is open.
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  if (!photo) return null;

  const onTouchStart = (e) => {
    touchStartX.current = e.changedTouches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 40) {
      if (dx < 0) onNavigate((index + 1) % count);
      else onNavigate((index - 1 + count) % count);
    }
    touchStartX.current = null;
  };

  const hasCaption =
    photo.title || photo.location || photo.description || photo.taken_at;

  return createPortal(
    <div
      className="lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={photo.title || "Photo viewer"}
      onClick={onClose}
    >
      <button className="lightbox-close" type="button" aria-label="Close" onClick={onClose}>
        ×
      </button>
      {count > 1 && (
        <button
          className="lightbox-nav lightbox-prev"
          type="button"
          aria-label="Previous photo"
          onClick={(e) => {
            e.stopPropagation();
            onNavigate((index - 1 + count) % count);
          }}
        >
          ‹
        </button>
      )}
      <figure
        className="lightbox-figure"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <img src={imageUrl(photo.full_key)} alt={photo.description || photo.title || "Photo"} />
        {hasCaption && (
          <figcaption className="lightbox-caption">
            {photo.title && <strong>{photo.title}</strong>}
            {photo.location && <span>{photo.location}</span>}
            {photo.taken_at && <time dateTime={photo.taken_at}>{photo.taken_at}</time>}
            {photo.description && <p>{photo.description}</p>}
          </figcaption>
        )}
      </figure>
      {count > 1 && (
        <button
          className="lightbox-nav lightbox-next"
          type="button"
          aria-label="Next photo"
          onClick={(e) => {
            e.stopPropagation();
            onNavigate((index + 1) % count);
          }}
        >
          ›
        </button>
      )}
    </div>,
    document.body
  );
};

export default Lightbox;
